import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import EnterpriseIcon, { StarFilledIcon, StarIcon } from '@enterprise-ui/icons';
import classnames from 'classnames';
import React__default, { useState, useEffect } from 'react';
import alertUserOfDeprecatedProps from '../../utils/deprecation.js';
import useCanvasId from '../../utils/useCanvasId.js';

var _excluded = ["averageValue", "className", "data-testid", "disabled", "id", "required", "size", "total", "value", "onChange", "error"];
var buildComponentReadOnly = function buildComponentReadOnly(averageValue, checkedIcon, dataTestid, total, uncheckedIcon, id) {
  var ratingsForeground = [];
  var ratingsBackground = [];
  for (var index = 0; index < total; index++) {
    ratingsForeground.push( /*#__PURE__*/React__default.createElement("span", {
      key: "readOnly_".concat(index),
      className: "C-Rating__Icon --readOnly"
    }, checkedIcon));
    ratingsBackground.push( /*#__PURE__*/React__default.createElement("span", {
      key: "readOnly_".concat(index),
      className: "C-Rating__Icon --readOnly"
    }, uncheckedIcon));
  }
  var currentSize = averageValue / total * 100;
  var background = /*#__PURE__*/React__default.createElement("div", {
    key: "rating_".concat(id, "-background"),
    className: "C-Rating__Background"
  }, ratingsBackground);
  var foreground = /*#__PURE__*/React__default.createElement("div", {
    "data-testid": dataTestid ? "".concat(dataTestid, "_readonly") : '',
    key: "rating_".concat(id, "-foreground"),
    className: "C-Rating__Foreground",
    style: {
      width: '' + currentSize + '%'
    }
  }, ratingsForeground);
  return [background, foreground];
};
var buildComponent = function buildComponent(currentValue, checkedIcon, dataTestid, disabled, id, onChange, required, setValue, total, uncheckedIcon) {
  if (!id) {
    console.error("Hey Canvas User! The Canvas Feedback component requires a unique id attribute because it is a form field.");
  }
  var handleOnChange = function handleOnChange(event) {
    setValue(event.target.value);
    onChange && onChange(event);
  };
  var ratingsSelectable = [];
  for (var index = 0; index < total; index++) {
    var ratingIndex = index + 1;
    var input = /*#__PURE__*/React__default.createElement("input", {
      id: "".concat(id, "_").concat(ratingIndex),
      key: "".concat(id, "_").concat(ratingIndex, "-input"),
      name: id,
      required: required,
      "data-testid": dataTestid ? "".concat(dataTestid, "_input") : null,
      value: ratingIndex,
      type: "radio",
      disabled: disabled,
      checked: ratingIndex.toString() === currentValue.toString(),
      "aria-label": ratingIndex.toString() === currentValue.toString() ? "Rated ".concat(ratingIndex, " of ").concat(total, ", checked") : "Rated ".concat(ratingIndex, " of ").concat(total),
      onChange: handleOnChange,
      className: "C-Rating__Input"
    });
    var label = /*#__PURE__*/React__default.createElement("label", {
      key: "".concat(id, "_").concat(ratingIndex, "-label"),
      htmlFor: "".concat(id, "_").concat(ratingIndex),
      "data-testid": dataTestid ? "".concat(dataTestid, "_label") : null,
      className: "C-Rating__Icon ".concat(disabled ? 'isDisabled' : '')
    }, ratingIndex <= currentValue ? checkedIcon : uncheckedIcon);
    ratingsSelectable.push(input, label);
  }
  var result = ratingsSelectable;
  return result;
};
var InputRating = function InputRating(_ref) {
  var averageValue = _ref.averageValue,
    className = _ref.className,
    dataTestid = _ref['data-testid'],
    disabled = _ref.disabled,
    id = _ref.id,
    required = _ref.required,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'normal' : _ref$size,
    _ref$total = _ref.total,
    total = _ref$total === void 0 ? 5 : _ref$total,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? 0 : _ref$value,
    onChange = _ref.onChange;
    _ref.error;
    var otherProps = _objectWithoutProperties(_ref, _excluded);
  alertUserOfDeprecatedProps('Rating', otherProps);
  var uuid = useCanvasId(id, 'input');
  var _useState = useState(value),
    _useState2 = _slicedToArray(_useState, 2),
    currentValue = _useState2[0],
    setValue = _useState2[1];
  var checkedIcon = /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    size: "inline",
    icon: StarFilledIcon
  });
  var uncheckedIcon = /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    size: "inline",
    icon: StarIcon
  });
  useEffect(function () {
    // makes sure that the component responds to outside changes to value
    setValue(value);
  }, [value]);
  var finalClasses = classnames('C-Rating', className, _defineProperty(_defineProperty(_defineProperty({}, "--size-".concat(size), size), '--has-selection', currentValue > 0), '--selectable', onChange || disabled));
  averageValue ? buildComponentReadOnly(averageValue, checkedIcon, dataTestid, total, uncheckedIcon, uuid) : buildComponent(currentValue, checkedIcon, dataTestid, disabled, uuid, onChange, required, setValue, total, uncheckedIcon);
  if (averageValue) {
    return /*#__PURE__*/React__default.createElement("section", _extends({
      "data-testid": dataTestid,
      className: finalClasses
    }, otherProps, {
      "aria-label": "Rated ".concat(averageValue)
    }), buildComponentReadOnly(averageValue, checkedIcon, dataTestid, total, uncheckedIcon, uuid));
  } else {
    return /*#__PURE__*/React__default.createElement("fieldset", _extends({
      "data-testid": dataTestid,
      className: finalClasses
    }, otherProps, {
      "aria-required": required ? 'true' : 'false'
    }), /*#__PURE__*/React__default.createElement("legend", {
      className: "hc-sr-only"
    }, currentValue ? "Rated ".concat(currentValue, " out of ").concat(total) : 'Unrated'), buildComponent(currentValue, checkedIcon, dataTestid, disabled, uuid, onChange, required, setValue, total, uncheckedIcon));
  }
};
InputRating.displayName = 'Rating';

export { InputRating as default };
